import React, { useEffect, useState } from 'react';
import { createAdminSeller, getAdminSupplier, recordAdminSupplierTransfer, saveAdminSupplierPrices } from '../api';
import ProductIdentity from './ProductIdentity';

const money = (value) => `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
const errorText = (error) => error.response?.data?.details?.[0]?.message || error.response?.data?.error || 'Попробуйте ещё раз.';
const purchaseStatus = { PENDING: 'Ожидает выдачи или сверки', AVAILABLE: 'К выплате', SETTLED: 'Перевод отмечен', REVERSED: 'Возврат или отмена' };

export function AdminCreateSeller({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  async function submit(event) {
    event.preventDefault();
    const fields = new FormData(event.currentTarget);
    setPending(true);
    setError('');
    try {
      const { data } = await createAdminSeller({
        name: fields.get('name'), email: fields.get('email'), password: fields.get('password'),
        managedByMaxMarket: fields.get('mode') === 'managed',
      });
      onCreated(data);
      setOpen(false);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setPending(false);
    }
  }

  return <section className="admin-supplier-panel">
    <button type="button" className="admin-supplier-button" aria-expanded={open} onClick={() => { setOpen(!open); setError(''); }}>
      {open ? 'Закрыть форму' : 'Создать продавца'}
    </button>
    {open && <form className="admin-supplier-fields" onSubmit={submit}>
      <label>Название магазина<input name="name" required maxLength={150} /></label>
      <label>Email для входа<input type="email" name="email" required maxLength={254} autoComplete="off" /></label>
      <label>Пароль<input type="password" name="password" required minLength={8} maxLength={72} autoComplete="new-password" /></label>
      <label>Режим работы<select name="mode" defaultValue="managed">
        <option value="managed">Поставщик MaxMarket</option>
        <option value="standard">Самостоятельный продавец</option>
      </select></label>
      <button className="admin-supplier-button" disabled={pending}>{pending ? 'Создаём…' : 'Создать кабинет'}</button>
      {error && <p role="alert">{error}</p>}
    </form>}
  </section>;
}

function SupplierPrices({ sellerId, variant, terms }) {
  const [purchase, setPurchase] = useState(variant.procurement ? String(variant.procurement.purchasePriceMinor / 100) : '');
  const [base, setBase] = useState(variant.price == null ? '' : String(variant.price));
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const retail = Math.floor(Number(base) / (1 - (terms.acquiringRate + terms.taxRate + terms.commissionRate)));

  async function submit(event) {
    event.preventDefault();
    setPending(true);
    setError('');
    setMessage('');
    try {
      const { data } = await saveAdminSupplierPrices(sellerId, variant.id, {
        purchasePriceMinor: Math.round(Number(purchase) * 100), basePriceMinor: Math.round(Number(base) * 100),
      });
      setMessage(`Сохранено. Цена на витрине ${money(data.retailPrice)}.`);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setPending(false);
    }
  }

  return <form className="admin-supplier-fields admin-supplier-sku" onSubmit={submit}>
    <div><strong>{variant.size}</strong><br /><small>Остаток {variant.stock} шт.</small></div>
    <label>Закупка, ₽<input type="number" min="0.01" step="0.01" required value={purchase} onChange={e => { setPurchase(e.target.value); setMessage(''); }} /></label>
    <label>Базовая цена продажи, ₽<input type="number" min="1" step="0.01" required value={base} onChange={e => { setBase(e.target.value); setMessage(''); }} /></label>
    <div><small>Цена на витрине</small><br /><strong>{retail > 0 ? money(retail) : '—'}</strong></div>
    <button className="admin-supplier-button" disabled={pending}>{pending ? 'Сохраняем…' : 'Сохранить цены'}</button>
    {message && <p role="status">{message}</p>}
    {error && <p role="alert">{error}</p>}
  </form>;
}

function SupplierTransfer({ sellerId, entryId, onSaved }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault();
    const reference = new FormData(event.currentTarget).get('reference');
    setPending(true);
    setError('');
    try {
      await recordAdminSupplierTransfer(sellerId, entryId, reference);
      await onSaved();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setPending(false);
    }
  }
  return <form className="admin-supplier-fields" onSubmit={submit}>
    <label>Номер выполненного перевода<input name="reference" required maxLength={120} /></label>
    <button className="admin-supplier-button" disabled={pending}>Отметить перевод</button>
    {error && <p role="alert">{error}</p>}
  </form>;
}

export default function AdminSupplierPanel({ seller, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const reload = React.useCallback(async () => {
    try {
      setError('');
      setData((await getAdminSupplier(seller.id)).data);
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [seller.id]);
  useEffect(() => { reload(); }, [reload]);

  return <section className="admin-supplier-panel" aria-label={`Управление поставщиком ${seller.name}`}>
    <div className="admin-supplier-heading"><h3>{seller.name}</h3><button type="button" onClick={onClose}>Закрыть</button></div>
    {error && <p role="alert">{error} <button type="button" onClick={reload}>Повторить</button></p>}
    {!data && !error && <p role="status">Загружаем данные поставщика…</p>}
    {data && <>
      <h4>Цены по размерам</h4>
      <p>Комиссия площадки {Number((data.terms.commissionRate * 100).toFixed(2)).toLocaleString('ru-RU')}%. Витрина рассчитывается от базовой цены продажи.</p>
      {!data.products.length && <p>Когда в кабинете поставщика появятся товары, здесь можно будет назначить цены.</p>}
      {data.products.map(product => <details key={product.id} className="admin-supplier-product">
        <summary><ProductIdentity product={product} size={44} zoom={false} /></summary>
        {product.variants.map(variant => <SupplierPrices key={variant.id} sellerId={seller.id} variant={variant} terms={data.terms} />)}
      </details>)}
      <h4>Расчёты с поставщиком</h4>
      <p>Последние начисления и возвраты. Отметку о переводе можно добавить после оплаты в банке.</p>
      {!data.purchases.length && <p>Начислений пока нет.</p>}
      {data.purchases.map(entry => <div className="admin-supplier-sku" key={entry.id}>
        <strong>Заказ №{entry.customerOrderId} · {money(entry.amountMinor / 100)}</strong>
        <p>{purchaseStatus[entry.status]}{entry.settlementEntries[0]?.settlement.transferReference && ` · ${entry.settlementEntries[0].settlement.transferReference}`}</p>
        {entry.status === 'AVAILABLE' && <SupplierTransfer sellerId={seller.id} entryId={entry.id} onSaved={reload} />}
      </div>)}
    </>}
  </section>;
}
